import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Alert, DialogActions } from "@mui/material";
import { useContext, useEffect, useState } from "react";

import { AuthContext } from "../../components/auth-provider";
import { CandidatesDict } from "../../types/recruiting";
import { Snackbar } from "@mui/material";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { saveCandidate } from "../../data-functions/recruiting_api";
import { sendCandidateEmail } from "../../utils/candidate-utils";

interface SendRejectedAfterInterviewDialogProps {
  candidates: CandidatesDict;
  open: boolean;
  onClose: () => void;
  onUpdated?: (updatedIds: string[]) => void;
}

const TEMPLATE_ID = "candidate_rejected_after_interview";

const SendRejectedAfterInterviewDialog = ({
  candidates: initialCandidates,
  open,
  onClose,
  onUpdated,
}: SendRejectedAfterInterviewDialogProps) => {
  const [openSnackError, setOpenSnackError] = useState(false);
  const [candidates, setCandidates] = useState<CandidatesDict>({});
  const [activePage, setActivePage] = useState(1);
  const [progress, setProgress] = useState(0);
  const [failedSends, setFailedSends] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [autoReject, setAutoReject] = useState(false);

  const { currentUser } = useContext(AuthContext)!;

  useEffect(() => {
    const newCandidates = { ...initialCandidates };
    setActivePage(1);
    setCandidates(newCandidates);
    setFailedSends([]);
    setProgress(0);
    setIsLoading(false);
  }, [initialCandidates]);

  const handleCloseSnack = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackError(false);
  };

  const handleClose = () => {
    onClose();
  };

  const sendRejectedEmails = async () => {
    setIsLoading(true);
    try {
      setProgress(0);
      setActivePage(2);
      setFailedSends([]);

      const candidateIds = Object.keys(candidates);
      const total = candidateIds.length;
      const batchSize = 200;
      let processedCount = 0;
      const updatedIds: string[] = [];

      for (let i = 0; i < total; i += batchSize) {
        const batch = candidateIds.slice(i, i + batchSize);
        for (const candidateId of batch) {
          try {
            const resp = await sendCandidateEmail([candidateId], TEMPLATE_ID);
            if (resp.status !== 200) {
              setFailedSends((prev) => [...prev, candidateId]);
            } else {
              const json = await resp.json().catch(() => undefined);
              const status = json?.data?.status;
              if (status === "OK") {
                if (autoReject) {
                  const updated = {
                    ...candidates[candidateId],
                    status: "rejected" as const,
                  };
                  await saveCandidate(currentUser?.appUser, {
                    id: candidateId,
                    candidate: updated,
                  });
                  updatedIds.push(candidateId);
                }
              } else {
                setFailedSends((prev) => [...prev, candidateId]);
              }
            }
          } catch (_err) {
            setFailedSends((prev) => [...prev, candidateId]);
          }
          processedCount += 1;
          setProgress(Math.round((processedCount / total) * 100));
        }
      }
      if (autoReject && updatedIds.length > 0 && onUpdated) {
        onUpdated(updatedIds);
      }
      setActivePage(3);
    } catch (_e) {
      setOpenSnackError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getActivePage = () => {
    switch (activePage) {
      case 1:
        return (
          <Grid container gap={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoReject}
                    onChange={(e) => setAutoReject(e.target.checked)}
                  />
                }
                label={
                  <Typography>
                    Automatically set status to <b>Rejected</b> for successfully
                    mailed candidates
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                disabled={Object.keys(candidates).length === 0 || isLoading}
                onClick={sendRejectedEmails}
              >
                {isLoading ? "Sending..." : "Send rejection emails"}
              </Button>
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container gap={2}>
            <Grid item xs={12}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }} component="span">
                Sending emails {progress}% of {Object.keys(candidates).length}
              </Typography>
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container gap={2}>
            <Grid item xs={12}>
              <Typography>
                {failedSends.length === 0
                  ? "Rejection emails sent successfully to all candidates."
                  : `Emails sent with some failures (${failedSends.length}).`}
              </Typography>
              {failedSends.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" color="error" gutterBottom>
                    Failed to send to candidates ({failedSends.length}):
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 150,
                      overflow: "auto",
                      bgcolor: "#f5f5f5",
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {failedSends.map((candidateId) => (
                      <Typography
                        key={"ri-" + candidateId}
                        variant="body2"
                        gutterBottom
                      >
                        {candidates[candidateId]?.email || candidateId}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"lg"}>
      <DialogTitle>Send Rejected After Interview Emails</DialogTitle>
      <DialogContent sx={{ minHeight: "100px", width: "700px" }}>
        {getActivePage()}
        <Snackbar
          open={openSnackError}
          autoHideDuration={2000}
          onClose={handleCloseSnack}
        >
          <Alert
            onClose={handleCloseSnack}
            severity="error"
            sx={{ width: "100%" }}
          >
            The server encountered an error.
          </Alert>
        </Snackbar>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendRejectedAfterInterviewDialog;
